import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { search } from 'sr-types';
import Stepper, { StepsIndexConstant } from '../components/Stepper';
import { useStepper } from '../components/StepperContext';
import { BUTTON_VARIANTS, PUNCTUATION_MARKS_CONSTANTS } from '../constants/constants';
import type { Entity } from '../constants/customSortFieldConfigs';
import FormModal from '../form/FormModal';
import I18n from '../i18n/I18n';
import { StylingProvider } from '../themeContext/StylingProvider';
import { Filter } from '../types/savedFilters';
import { failedToastMessage } from '../utils/commonUtils';
import type { EntityReportAdapter, ReportTemplateDataValue } from './adapters/EntityReportAdapter';
import CustomizeReport from './CustomizeReport';
import PreviewReport from './PreviewReport';
import ReportConfigureStep from './ReportConfigureStep';
import ReportTemplateStep from './ReportTemplateStep';
import type { GroupOption } from './reportTypes';
import { buildSortTokens, extractReportConfigurations } from './reportUtils';

interface GenerateReportInterface<TRawRow extends Record<string, unknown> = Record<string, unknown>> {
    setReportVisible: Dispatch<SetStateAction<boolean>>;
    reportVisible: boolean;
    latestSearchResults?: TRawRow[];
    previewData?: ReportTemplateDataValue;
    sortOrders?: (string | number)[][];
    reportLoading?: boolean;
    onFetchReportData?: (overrides?: { sortBy?: string }) => void | Promise<void>;
    facets?: search.SkFacetSet[];
    appliedFilters?: Filter[];
    entityReportAdapter: EntityReportAdapter<TRawRow>;
    preferenceEntityKey: string;
    reportContextTerm?: string;
}

export default <TRawRow extends Record<string, unknown> = Record<string, unknown>>({
    setReportVisible,
    reportVisible,
    latestSearchResults,
    previewData,
    sortOrders,
    onFetchReportData,
    reportLoading,
    facets,
    appliedFilters,
    entityReportAdapter,
    preferenceEntityKey,
    reportContextTerm
}: GenerateReportInterface<TRawRow>) => {
    const { activeStep, getStepData, setStepCompletion, goToNextStep } = useStepper();
    const templateStepIndex = StepsIndexConstant.zero;
    const customizeStepIndex = StepsIndexConstant.two;
    const previewStepIndex = StepsIndexConstant.three;
    const [isExporting, setIsExporting] = useState(false);
    const exportHandlerRef = useRef<null | (() => Promise<void>)>(null);
    const templateSectionStep = getStepData(templateStepIndex);
    const configureStepData = getStepData(StepsIndexConstant.one);
    const entity: Entity = entityReportAdapter.entity;
    const [reportTitle, setReportTitle] = useState<string>(templateSectionStep?.reportTitle || '');
    const reportConfigurations = useMemo(
        () => extractReportConfigurations(configureStepData || undefined, entity),
        [configureStepData, entity]
    );

    const onRegisterExport = useCallback((fn?: () => Promise<void>) => {
        if (fn) {
            exportHandlerRef.current = async () => {
                try {
                    setIsExporting(true);
                    await fn();
                } finally {
                    setIsExporting(false);
                }
            };
        } else {
            exportHandlerRef.current = null;
        }
    }, []);

    const steps = useMemo(
        () => [
            {
                label: 'report.stepper.template',
                component: () => <ReportTemplateStep entity={entity} preferenceEntityKey={preferenceEntityKey} />
            },
            {
                label: 'report.stepper.template.configure',
                component: () => <ReportConfigureStep entityReportAdapter={entityReportAdapter} />
            },
            {
                label: 'report.stepper.customize',
                component: () => (
                    <CustomizeReport
                        facets={facets}
                        appliedFilters={appliedFilters}
                        entity={entity}
                        preferenceEntityKey={preferenceEntityKey}
                        reportContextTerm={reportContextTerm}
                        setReportTitle={setReportTitle}
                    />
                )
            },
            {
                label: 'report.stepper.preview',
                component: () => (
                    <PreviewReport
                        latestSearchResults={latestSearchResults}
                        previewData={previewData}
                        sortOrders={sortOrders}
                        onRegisterExport={onRegisterExport}
                        entityReportAdapter={entityReportAdapter}
                        preferenceEntityKey={preferenceEntityKey}
                        reportContextTerm={reportContextTerm}
                        reportTitle={reportTitle}
                    />
                )
            }
        ],
        [
            appliedFilters,
            entityReportAdapter,
            facets,
            latestSearchResults,
            onRegisterExport,
            previewData,
            preferenceEntityKey,
            reportContextTerm,
            entity,
            sortOrders
        ]
    );
    const isLastStep = activeStep === steps.length - 1;
    const isCustomizeStep = activeStep === customizeStepIndex;
    const isPreviewStep = activeStep === previewStepIndex;

    const resolveGroupSelections = (): GroupOption[] => {
        return reportConfigurations.workspaceGroupBy;
    };

    const handleCustomizeStepClick = async () => {
        const groupSelections = resolveGroupSelections();

        let sortBy: string | undefined;

        if (!groupSelections.length) {
            sortBy = reportConfigurations.sortWorkspacesBy || undefined;
        } else {
            const sortTokens = buildSortTokens(groupSelections, reportConfigurations, entity) || [];
            if (sortTokens.length) {
                sortTokens.push(reportConfigurations.sortCohortsBy);
                sortBy = sortTokens.join(PUNCTUATION_MARKS_CONSTANTS.comma);
            }
        }

        if (onFetchReportData) {
            await onFetchReportData(sortBy ? { sortBy } : undefined);
        }

        goToNextStep();
    };

    const additionalActions = [
        {
            label: <I18n token={isCustomizeStep ? 'form.next' : 'actions.download'} />,
            onClick: async () => {
                if (isCustomizeStep) {
                    try {
                        await handleCustomizeStepClick();
                    } catch {
                        failedToastMessage('report.load.failed');
                    }
                    return;
                }
                const fn = exportHandlerRef.current;
                if (!fn) {
                    console.warn('Export handler not registered yet');
                    return;
                }

                fn()
                    .then(() => setReportVisible(false))
                    .catch((err) => failedToastMessage(err, 'report.failed.export'));
            },
            hidden: !isCustomizeStep && !isPreviewStep,
            variant: BUTTON_VARIANTS.contained,
            loading: reportLoading || isExporting
        }
    ];

    useEffect(() => {
        const hasSelectedTemplate = Boolean(templateSectionStep?.selectedId);
        const hasTitle = Boolean(templateSectionStep?.reportTitle?.trim());
        setStepCompletion(templateStepIndex, hasSelectedTemplate && hasTitle);
    }, [setStepCompletion, templateStepIndex, templateSectionStep?.reportTitle, templateSectionStep?.selectedId]);

    return (
        // StylingProvider with forceMode="light" overrides the parent provider to force the report modal to always use light mode.
        <StylingProvider forceMode="light">
            <FormModal
                id={'generate-report-modal'}
                title={<I18n token="report.stepper.create" />}
                isOpen
                hideButtons
                hideDefaultButtons
                onClose={() => setReportVisible(false)}
                width="80%"
            >
                <Stepper
                    steps={steps}
                    additionalActions={additionalActions}
                    hideDefaultNextButton={isCustomizeStep || isLastStep}
                />
            </FormModal>
        </StylingProvider>
    );
};
